import React, { useState, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';

import { ALLOWED_QUERY_OPERATOR } from '/src/lib/constants';
import fundingAggregationsApi from '/src/apis/fundingAggregationsApi';
import AmplitudeHelperClass from '/src/lib/amplitudeHelper';
import WorldMapChart from '/src/MicroFrontends/D3Charts/WorldMapChart';
import { withBootstrapPlugin } from '/src/lib/helpers';



const amplitudeHelper = new AmplitudeHelperClass();

export const HeroSection = (props) => {

    const { pageProps } = props;
    const { map_type, region_id, type, investment_type_id, investment_vehicle_id } = pageProps;

    // get colors based on page type
    const getPageColors = () => {
        // PE Investment page (id: 202)
        if (pageProps.id === 202) {
            return {
                COLOR_01_RGB: [34, 101, 179],   // #2265b3
                COLOR_02_RGB: [230, 240, 252]   // lighter shade of #2265b3
            };
        }

        // Default colors for other pages (VC, Venture Debt, regions)
        return {
            COLOR_01_RGB: [210, 34, 72],   // #d22248
            COLOR_02_RGB: [254, 231, 232]  // #fee7e8
        };
    };

    const { COLOR_01_RGB, COLOR_02_RGB } = getPageColors();

    // generate color gradient based on number of required countries
    const generateColorTable = (steps) => {
        const start = COLOR_01_RGB;
        const end   = COLOR_02_RGB;

        const colors = [];
        for (let i = 0; i < steps; i++) {
            const t = i / (steps - 1);
            const r = Math.round(start[0] + (end[0] - start[0]) * t);
            const g = Math.round(start[1] + (end[1] - start[1]) * t);
            const b = Math.round(start[2] + (end[2] - start[2]) * t);
            colors.push(`rgb(${r}, ${g}, ${b})`);
        }
        return colors;
    }

    const [worldMapData, setWorldMapData] = useState({
        value: (d) => d.value,
        tooltip_keys: ['Geography', 'Number of Deals', 'Value of Deals', 'Share (%)'],
        format_values: true,
        value_param: "amount",
        name_param: "investedin_location_name",
        hide_legend: true,
        condense_data: false, // avoid condensing data and creating 'others' category
        map_type: map_type ? map_type : 'world'
    });

    useEffect(() => {
        getWorldMapData();
    }, []);

    const getWorldMapData = async () => {
        let filters = [];

        // region filter
        if (type === 'region' && region_id.length > 0) {
            filters.push({
                key: 'region_filter', 
                value: [region_id], 
                op: ALLOWED_QUERY_OPERATOR.equal
            })
        }

        // investment type filter (vc, pe)
        if (type === 'investment_type' && investment_type_id?.length > 0 && [201, 202].includes(pageProps.id)) {
            filters.push({
                key: 'investment_type_filter', 
                value: [investment_type_id], 
                op: ALLOWED_QUERY_OPERATOR.equal
            })
        }

        // investment vehicle filter (venture debt)
        if (type === 'investment_type' && investment_vehicle_id?.length > 0 && [203].includes(pageProps.id)) {
            filters.push({
                key: 'investment_vehicle_filter', 
                value: [investment_vehicle_id], 
                op: ALLOWED_QUERY_OPERATOR.equal
            })
        }

        await fundingAggregationsApi.getInvestmentAnalytics(filters).then((response) => {
            if (!isEmpty(response)) {
                setWorldMapData(prevData => ({ 
                    ...prevData, 
                    dataset: response,
                    color_table: generateColorTable(response.length),
                }));
            }
        })
    }

    const showContactForm = () => {
        let amplitudeString = '';
        if (type === 'region') {
            amplitudeString = 'Clicked Contact Sales from Regional Data Landing Page';
        } else if (type === 'investment_type') {
            amplitudeString = 'Clicked Contact Sales from Investment Type Data Landing Page';
        }

        amplitudeHelper.trackEvent(amplitudeString, {
            page_type: pageProps.dataType
        });

        withBootstrapPlugin('modal', ($) => $("#contactFormModal").modal('show'));
    }

    let title    = 'Make Smarter Moves with Real-Time Data';    // generic version
    let subtitle = "Let's talk - contact us for a custom demo." // generic version

    if (type === 'region') {
        if (pageProps.id === 101) {
            title    = 'Make Smarter Moves in MENA with Real-Time Data';
            subtitle = "Let's talk MENA - contact us for a custom demo.";
        } else if (pageProps.id === 102) {
            title    = 'Real-Time Africa Data, Guiding Your Next Big Decision';
            subtitle = "Tap into Africa's potential and get in touch now.";
            // subtitle: "Want to explore Africa's data landscape? Contact us!""
        } else if (pageProps.id === 103) {
            title    = 'Southeast Asia Unlocked - Smart Moves Start with Data';
            subtitle = "Want the edge in Southeast Asia? Let's connect."
        }
    }

    if (type === 'investment_type') {
        if (pageProps.id === 201) {
            title    = "Your Competitive Edge in Venture Capital";
            // title = "Find the Next Big Deal with Venture Capital Intelligence";
            subtitle = "Your next deal starts with better intelligence - so reach out now.";
        } else if (pageProps.id === 202) {
            title    = "Reliable Intelligence for Smarter Private Equity Decisions";
            subtitle = "Contact us to see how we help Private Equity teams win with data.";
        } else if (pageProps.id === 203) {
            title    = "Lead Strategically with Real-Time Venture Debt Insights";
            subtitle = "Want to take the uncertainty out of venture debt? Get in touch.";
        }
    }

    return (
        <div className="section-featured-graphic">
            <div className="row no-gutters">

                <div className="col-xl-6 col-lg-7 col-12 d-flex align-items-center">
                    <div className="column-text">
                        <div className="page-header text-left">
                            <h2>{title}</h2>
                            <p>
                                Get reliable, verified {pageProps.dataType} data in formats that fit your workflow, including interactive charts, downloadable reports, APIs and more.
                                {" "}
                                {subtitle}
                            </p>
                        </div>
                        <button className="btn btn-main clear-outline btn-lg" onClick={() => showContactForm()}>
                            <i className="fas fa-paper-plane"></i>Learn More
                        </button>
                    </div>
                </div>

                <div className="col-xl-6 col-lg-5 col-12 column-graphic" data-map-type={map_type}>
                    {worldMapData.dataset &&
                        <React.Fragment>

                            {/* chart */}
                            <WorldMapChart data={worldMapData} />

                            {/* indicator that chart can be hovered */}
                            <div id="hover-hint" className="hint-tooltip">
                                <i className="fas fa-mouse-pointer"></i>
                                {/* fas fa-compass */}
                                {' '}
                                {window.innerWidth < 1000 ? 
                                    'Tap a country to explore our data'
                                    :
                                    'Hover over a country to explore our data'
                                }
                            </div>

                            {/* chart legend */}
                            {worldMapData.color_table &&
                                <div className="map-legend">
                                    <div className="legend-label">Higher<br />deal value</div>
                                    <div className="legend-gradient" 
                                        style={{ background: `linear-gradient(to bottom, 
                                            rgb(${COLOR_01_RGB}), 
                                            rgb(${COLOR_02_RGB})
                                        )`}}
                                    ></div>
                                    <div className="legend-label">Lower<br />deal value</div>
                                </div>
                            }

                        </React.Fragment>
                    }
                </div>
                
            </div>
        </div>
    )

}
